import { useState } from "react";
import { motion } from "framer-motion";
import { Send, MessageCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { COUNTIES, BUSINESS_TYPES } from "@/data/content";

const WA_NUMBER = "40740433894";

export default function LeadForm({ compact = false }) {
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    county: "",
    business_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildWhatsAppLink = () => {
    const txt = `Salut DOTAPAN!\n\nNume: ${form.name}\nCompanie: ${form.company || "-"}\nTelefon: ${form.phone}\nEmail: ${form.email || "-"}\nJudet: ${form.county || "-"}\nTip afacere: ${form.business_type || "-"}\n\nMesaj: ${form.message || "-"}`;
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(txt)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.name.length < 2 || !form.phone || form.phone.length < 4) {
      toast.error("Te rugăm completează numele și telefonul.");
      return;
    }
    setLoading(true);
    try {
      // Strip empty optional fields so backend EmailStr / county / business_type don't reject them
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      await api.post("/leads", payload);
      toast.success("Mulțumim! Te contactăm cât de repede putem.");
      // open WhatsApp pre-filled
      const wa = buildWhatsAppLink();
      window.open(wa, "_blank", "noopener");
      setForm({ name: "", company: "", phone: "", email: "", county: "", business_type: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="lead-name" className="text-xs uppercase tracking-[0.2em] text-text-soft">Nume *</Label>
          <Input id="lead-name" data-testid="lead-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Ion Popescu" className="h-12 rounded-xl border-border focus:border-gold" required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lead-company" className="text-xs uppercase tracking-[0.2em] text-text-soft">Companie</Label>
          <Input id="lead-company" data-testid="lead-company" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Restaurant XYZ SRL" className="h-12 rounded-xl border-border focus:border-gold" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lead-phone" className="text-xs uppercase tracking-[0.2em] text-text-soft">Telefon *</Label>
          <Input id="lead-phone" data-testid="lead-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+40 7xx xxx xxx" className="h-12 rounded-xl border-border focus:border-gold" required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lead-email" className="text-xs uppercase tracking-[0.2em] text-text-soft">Email</Label>
          <Input id="lead-email" data-testid="lead-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="contact@firma.ro" className="h-12 rounded-xl border-border focus:border-gold" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-text-soft">Județ</Label>
          <Select value={form.county || undefined} onValueChange={(v) => update("county", v)}>
            <SelectTrigger className="h-12 rounded-xl border-border focus:border-gold" data-testid="lead-county">
              <SelectValue placeholder="Selectează județul" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {COUNTIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-text-soft">Tip afacere</Label>
          <Select value={form.business_type || undefined} onValueChange={(v) => update("business_type", v)}>
            <SelectTrigger className="h-12 rounded-xl border-border focus:border-gold" data-testid="lead-business-type">
              <SelectValue placeholder="Restaurant, cafenea, ..." />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {BUSINESS_TYPES.map((b) => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="lead-message" className="text-xs uppercase tracking-[0.2em] text-text-soft">Mesaj</Label>
        <Textarea
          id="lead-message"
          data-testid="lead-message"
          rows={compact ? 3 : 5}
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Spune-ne ce produse te interesează și volumul estimat..."
          className="rounded-xl border-border focus:border-gold resize-none"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <Button type="submit" disabled={loading} className="btn-gold rounded-full h-12 px-7 flex-1 sm:flex-none" data-testid="lead-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {loading ? "Se trimite..." : "Solicită ofertă personalizată"}
        </Button>
        <a
          href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Salut DOTAPAN, sunt interesat de produsele voastre pentru HoReCa.")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-[#25d366]/40 text-[#25d366] hover:bg-[#25d366]/10 transition-colors text-sm font-medium"
          data-testid="lead-whatsapp-direct"
        >
          <MessageCircle className="h-4 w-4" />
          Sau direct pe WhatsApp
        </a>
      </div>
      <p className="text-[11px] text-text-soft pt-1">
        Prin trimiterea formularului, ești de acord cu Politica noastră de confidențialitate.
      </p>
    </form>
  );
}
